// ─── Les budgets du mois, en ARBRE : budget → prévisions ─────────
// Demandé par Cédric le 14/08 : voir les opérations prévues sous chaque
// budget, et grisées quand elles ne s'appliquent pas au mois affiché.
//
// Pourquoi les GRISER plutôt que les masquer : une prévision trimestrielle
// disparaissait purement et simplement onze mois sur douze. On se demandait
// alors si elle avait été supprimée, mal saisie, ou si l'écran mentait. Grisée,
// elle dit « j'existe, mais pas ce mois-ci » — et sa raison est affichée.
//
// L'enveloppe reste cliquable (elle ouvre son plan) ; c'est le CHEVRON qui
// plie. Deux gestes distincts sur une même ligne, chacun sa zone.
import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { LigneSwipe, useUI, useT, confirmerSuppression } from '@mop/core';
import { formatEuro, montantOperation } from './services/calculs';
import { occurrencesDuMois, nonDuesDuMois, lignesDeBudget } from './services/occurrences';
import { emojiBudget, emojiCategorie, cleCategorie } from './data/categories';
import { statutDe } from './data/statuts';
import EnTeteArbre from './EnTeteArbre';

const libelleMois = (mk, t) => {
  const [y, m] = String(mk || '').split('-').map(Number);
  const noms = t('commun.moisLongs');
  return `${(Array.isArray(noms) ? noms[m - 1] : '') || mk || ''} ${y || ''}`.trim();
};

export default function ArbreBudgetsMois({
  budgets = [], lignes = [], exceptions = [], actuals = [], rattachements = [],
  mois, prevuParBudget, sansBudget = 0,
  onOuvrir, onOuvrirSansBudget, onEditerBudget, onSupprimerBudget, onFicheLigne,
  // Les FEUILLES se gèrent aussi au swipe (audit du 15/08) : → éditer la
  // prévision, ← la supprimer — comme partout ailleurs.
  onEditerLigne, onSupprimerLigne,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  // Replies d'emblee : « je veux avoir les ecrans epures » (Cedric, 15/08).
  // Ce qu'on deplie s'affiche en entier — le pliage RANGE, il ne cache pas.
  const [replies, setReplies] = useState(null);   // null = pas encore initialise
  const cles = [...budgets.map(b => b.id), null];
  const repliesEffectifs = replies ?? new Set(cles);
  const basculer = (cle) => setReplies((prev) => {
    const n = new Set(prev ?? cles);
    if (n.has(cle)) n.delete(cle); else n.add(cle);
    return n;
  });

  // Ce que chaque enveloppe porte ce mois-ci : les échéances DUES, puis les
  // prévisions qui existent sans tomber ce mois — grisées, avec leur raison.
  const contenu = useMemo(() => {
    const par = new Map();
    for (const cle of [...budgets.map(b => b.id), null]) {
      const mesLignes = lignesDeBudget(lignes, cle);
      if (!mesLignes.length) { par.set(cle, { dues: [], nonDues: [] }); continue; }
      par.set(cle, {
        dues: occurrencesDuMois({ lignes: mesLignes, actuals, exceptions, rattachements, mk: mois }),
        nonDues: nonDuesDuMois({ lignes: mesLignes, exceptions, mk: mois }),
      });
    }
    return par;
  }, [budgets, lignes, exceptions, actuals, rattachements, mois]);

  const Prevision = ({ o, grisee }) => (
    <LigneSwipe espacement={5} enabled
      onEditer={onEditerLigne ? () => onEditerLigne(o.ligne) : undefined}
      onSupprimer={onSupprimerLigne ? () => confirmerSuppression(
        t('finances.actSupprimerLigne'), o.ligne?.libelle || '',
        () => onSupprimerLigne(o.ligne)) : undefined}>
    <TouchableOpacity style={[s.ligne, grisee && s.ligneGrisee]} activeOpacity={0.7}
      onPress={() => onFicheLigne?.(o.ligne)}>
      <Text style={[s.ligneIc, grisee && s.pale]}>
        {grisee ? emojiCategorie(o.ligne?.categorie) : statutDe(o).emoji}
      </Text>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={[s.ligneLbl, grisee && s.pale]} numberOfLines={1}>
          {o.ligne?.libelle || t(cleCategorie(o.ligne?.categorie))}
        </Text>
        {/* La RAISON de l'absence : « pas due ce mois », « terminée »… Sans
            elle, une ligne grisée serait une énigme. */}
        {grisee && !!o.motif && (
          <Text style={s.ligneRaison} numberOfLines={1}>
            {t(`finances.nd_${o.motif}`, { mois: libelleMois(o.mois, t) })}
          </Text>
        )}
      </View>
      <Text style={[s.ligneMontant, grisee && s.pale,
        o.ligne?.sens === 'revenu' && !grisee && { color: UI.ok || '#2e7d32' }]}>
        {montantOperation(o.prevu ?? o.ligne?.montant, o.ligne?.sens)}
      </Text>
    </TouchableOpacity>
    </LigneSwipe>
  );

  const Groupe = ({ cle, budget, nb }) => {
    const c = contenu.get(cle) || { dues: [], nonDues: [] };
    const plie = repliesEffectifs.has(cle);
    const total = prevuParBudget?.get(cle) || 0;
    // Grammaire commune (15/08) : rangée plate — le chevron PLIE, le corps
    // OUVRE l'enveloppe, le total du mois à droite. Le sous-titre garde ce
    // que la carte disait : le hors-mois et les payeurs.
    const entete = (
      <EnTeteArbre plie={plie} onBasculer={() => basculer(cle)}
        emoji={budget ? emojiBudget(budget) : '❓'}
        titre={budget ? budget.nom : t('budget.sansBudget')}
        nb={c.dues.length}
        sousTitre={[
          c.nonDues.length ? `${c.nonDues.length} ${t('finances.horsMois')}` : '',
          budget ? `${(budget.payeurs || []).length} 👤` : '',
        ].filter(Boolean).join('  ·  ') || null}
        onCorps={() => (cle === null ? onOuvrirSansBudget?.() : onOuvrir?.(cle))}
        droite={(
          <Text style={[s.total, total < 0 && { color: UI.danger || '#c0392b' },
            total > 0 && { color: UI.ok || '#2e7d32' }]}>{formatEuro(total)}</Text>
        )} />
    );
    return (
      <View>
        {/* Confirmation ET verrou (« pas de suppression tant qu'il reste
            des lignes », 15/08) vivent chez le parent : une seule règle,
            deux vues qui l'appellent. */}
        {/* espacement={0} : la rangée porte déjà son marginTop — les 8 px
            du swipe s'y AJOUTAIENT, et Budget respirait plus large que
            Suivi pour la même grammaire (constaté par Cédric, 15/08). */}
        {budget ? (
          <LigneSwipe espacement={0} enabled
            onEditer={() => onEditerBudget?.(budget)}
            onSupprimer={() => onSupprimerBudget?.(budget)}>
            {entete}
          </LigneSwipe>
        ) : entete}

        {!plie && (
          <View style={s.enfants}>
            {c.dues.map(o => <Prevision key={o.ligneId} o={o} />)}
            {c.nonDues.map(o => <Prevision key={`x-${o.ligneId}`} o={o} grisee />)}
            {!c.dues.length && !c.nonDues.length && (
              <Text style={s.vide}>{t('finances.aucuneLigne')}</Text>
            )}
          </View>
        )}
      </View>
    );
  };

  return (
    <View>
      {budgets.map(b => <Groupe key={b.id} cle={b.id} budget={b} />)}
      {sansBudget > 0 && <Groupe key="__sans__" cle={null} budget={null} />}
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  total: { fontSize: 14, fontWeight: '800', color: UI.ink },
  enfants: { marginLeft: 22, marginTop: 2, marginBottom: 8 },
  // Plus de marginBottom : l'espacement vient de LigneSwipe (espacement={5}),
  // une marge sur la carte ferait dépasser les boutons de swipe.
  ligne: { flexDirection: 'row', alignItems: 'center', gap: 9, backgroundColor: UI.card, borderRadius: 10, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 8, paddingHorizontal: 11 },
  // Grisée = elle EXISTE mais ne tombe pas ce mois. Bordure en pointillés :
  // le même signal que la carte « Sans budget » — présent mais pas actif.
  ligneGrisee: { borderStyle: 'dashed', backgroundColor: 'transparent' },
  pale: { opacity: 0.45 },
  ligneIc: { fontSize: 15 },
  ligneLbl: { fontSize: 13.5, fontWeight: '700', color: UI.ink },
  ligneRaison: { fontSize: 11, color: UI.faint, marginTop: 1 },
  ligneMontant: { fontSize: 13.5, fontWeight: '800', color: UI.ink },
  vide: { fontSize: 12, color: UI.faint, paddingVertical: 6 },
});
